import { inject, Injectable } from '@angular/core';
import { TokenUserContext } from '@app/core/models/auth.model';
import { Events } from '@app/core/models/event-map';
import { PublishSubscribeService } from '@app/core/services/publish-subscribe.service';
import { PortalUserState } from '@app/shared/models/access-control/portal-user-state.model';
import { Customer } from '@app/shared/models/customer.model';
import { PortalPermissionRole } from '@app/shared/models/portal-permission-role.model';
import { PortalUser } from '@app/shared/models/portal-user.model';
import { Contactperson } from '@app/shared/models/user.model';
import { catchError, finalize, forkJoin, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { CustomerApiService } from '../customer-api.service';
import { ConstantsService } from '../internal-services/constants-internal.service';
import { PortalApiService } from '../portal-api.service';

@Injectable({
  providedIn: 'root',
})
export class PortalUserService {

  #portalApiService = inject(PortalApiService);
  #customerApiService = inject(CustomerApiService)
  #publishSubscribeService = inject(PublishSubscribeService)

  private userState: PortalUserState = undefined

  get state(): PortalUserState | undefined {
    return this.userState;
  }

  init(tokenUserCtx: TokenUserContext): Observable<void> {

    return this.setByTokenUserContext(tokenUserCtx).pipe(
      switchMap(() =>
        forkJoin([
          this.loadPermissionRoles(),
          this.loadSvfCustAccountCustPortal(),
          this.loadCustTables_SP_TR_CC(),
          this.loadContactPersons_SP_TR_CC()
        ])
      ),
      map(() => void 0),
      catchError((err) => {
        console.error(`Failed to init portal user`, err)
        return of(void 0)
      })
    )
  }

  loadUsers(adUserId: string, adDomain: string): Observable<void> {
    const includeBlank: boolean = false
    const includeShowAll: boolean = true

    return this.#portalApiService.getPortalUsers(adUserId, adDomain, includeBlank, includeShowAll).pipe(
      tap((response: PortalUser[]) => {
        this.userState.portalUsers = response
      }),
      map(() => void 0),
      finalize(() => this.#publishSubscribeService.trigger(Events.UserService.PORTAL_USERS_CHANGED)),
      catchError((err) => {
        console.error(`Failed to load portal users`, err?.message)
        return throwError(() => new Error(`Failed to load portal users`))
      })
    )
  }

  loadNewWebQuotationStartsAt(): Observable<void> {
    return this.#customerApiService.getNewWebQuotationStartsAt().pipe(
      tap((response: { NewWebQuotationStartsAt: number }) => {

        if (response.NewWebQuotationStartsAt === undefined || response.NewWebQuotationStartsAt === null) {
          throw new Error(`Unexpected error while fetching getNewWebQuotationStartsAt.`)
        }

        this.userState.newWebQuotationStartsAt = response.NewWebQuotationStartsAt
      }),
      map(() => void 0),
      catchError((err) => {
        console.error(`loadNewWebQuotationStartsAt`, err?.message)
        return throwError(() => new Error(`Failed to get new web quotation starts at`))
      })
    )
  }

  private loadPermissionRoles(): Observable<void> {
    return this.#portalApiService.getPortalPermissionRole().pipe(
      tap((response: PortalPermissionRole[]) => this.userState.portalPermissionRoles = response),
      map(() => void 0),
      catchError((err) => {
        console.error(`loadPortalPermissionRoles`, err)
        return of(void 0)
      })
    )
  }

  private loadSvfCustAccountCustPortal(): Observable<void> {
    return this.#portalApiService.getSvfCustAccountCustPortal(this.userState.svfAccount).pipe(
      tap((response) => this.userState.custAccountCustPortal = response),
      map(() => void 0),
      catchError((err) => {
        console.error(`loadSvfCustAccountCustPortal`, err)
        return of(void 0)
      })
    )
  }

  private loadCustTables_SP_TR_CC(): Observable<void> {
    const sp = this.userState.accountNum_SP
    const tr = this.userState.accountNum_TR
    const cc = this.userState.accountNum_CC

    return this.#customerApiService.getCustomerTablesSpTrCc(sp, tr, cc).pipe(
      tap((response: Customer[]) => {
        response.forEach(c => {
          const accountNum = c.ACCOUNTNUM.toLowerCase()

          if (accountNum === sp.toLowerCase()) this.userState.custTable_SP = c
          if (accountNum === tr.toLowerCase()) this.userState.custTable_TR = c
          if (c.ACCOUNTNUM === cc) this.userState.custTable_CC = c
        })
      }),
      map(() => void 0),
      catchError((err) => {
        console.error(`loadCustTables_SP_TR_CC`, err)
        return of(void 0)
      }),
      finalize(() => {
        this.setSalesConfirmEdi()
      })
    )
  }

  private loadContactPersons_SP_TR_CC(): Observable<void> {
    const sp = this.userState.contactPersonId_SP
    const tr = this.userState.contactPersonId_TR
    const cc = this.userState.contactPersonId_CC

    return this.#customerApiService.getContactPersonsSpTrCc(sp, tr, cc).pipe(
      tap((response: Contactperson[]) => {
        response.forEach(c => {
          if (c.CONTACTPERSONID === sp) this.userState.contactPerson_SP = c
          if (c.CONTACTPERSONID === tr) this.userState.contactPerson_TR = c
          if (c.CONTACTPERSONID === cc) this.userState.contactPerson_CC = c
        })
      }),
      map(() => void 0),
      catchError((err) => {
        console.error(`loadContactPersons_SP_TR_CC`, err)
        return of(void 0)
      })
    )
  }

  private clear(): void {
    this.userState = undefined
  }

  private setByTokenUserContext(user: TokenUserContext): Observable<void> {
    if (!user) {
      this.clear()
      return of(void 0)
    }

    this.userState = {
      userFullName: user.Fullname,
      brandSP: user.HasBrandSp,
      brandTR: user.HasBrandTr,
      brandCC: user.HasBrandCc,
      preSelectedBrand: user.PreSelectedBrand,
      axUserId: user.AxUserId,
      accountNum_SP: user.AccountNumSp,
      accountNum_TR: user.AccountNumTr,
      accountNum_CC: user.AccountNumCc,
      svfAccount: user.SvfAccount,
      contactPersonId_SP: user.ContactPersonIdSp,
      contactPersonId_TR: user.ContactPersonIdTr,
      contactPersonId_CC: user.ContactPersonIdCc,
      contactPerson_SP: undefined,
      contactPerson_TR: undefined,
      contactPerson_CC: undefined,
      userRole: user.UserRole,
      allowFieldChanges: user.AllowFieldChanges,
      allowDisplacements: user.AllowDisplacements,
      showPricesValue: user.ShowPricesValue,
      showVat: false,
      showUValue: true,
      custAccountCustPortal: undefined,
      custTable_SP: undefined,
      custTable_TR: undefined,
      custTable_CC: undefined,
      isEnabled_SalesConfirmEdiType_Trygg: false,
      newWebQuotationStartsAt: ConstantsService.NewWebQuotationStartsAt.ENUM_SELECT_FIRST_ITEM,
      networkAlias: user.NetworkAlias,
      networkDomain: user.NetworkDomain,
      portalPermissionRoles: undefined,
      portalUser: undefined,
      portalUsers: undefined
    }

    return of(void 0)
  }

  private setSalesConfirmEdi(): void {
    this.userState.isEnabled_SalesConfirmEdiType_Trygg = false

    if (this.isSalesConfirmEdiEnabled(this.userState.custTable_SP)) {
      this.processSalesConfirmEdiFlags(this.userState.custTable_SP)
    }

    if (this.isSalesConfirmEdiEnabled(this.userState.custTable_TR)) {
      this.processSalesConfirmEdiFlags(this.userState.custTable_TR)
    }
  }

  private processSalesConfirmEdiFlags(custTable: Customer) {
    const ediLookup: Record<string, number[]> = {
      trygg: [
        ConstantsService.SalesConfirmEdi.EdiType.ENUM_TRYGG,
        ConstantsService.SalesConfirmEdi.EdiType.ENUM_TRYGG_ONLYONCE
      ]
    }

    const ediType = custTable.CAP_SALESCONFIRMEDITYPE;

    if (ediLookup['trygg'].includes(ediType)) this.userState.isEnabled_SalesConfirmEdiType_Trygg = true
  }

  private isSalesConfirmEdiEnabled(custTable: Customer): boolean {
    return (
      !!custTable &&
      custTable.CAP_SALESCONFIRMEDIENABLE > ConstantsService.SalesConfirmEdi.EdiEnable.ENUM_NONE
    )
  }
}
